import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useIsVisible } from 'react-is-visible';
import { useIntervalWhen } from "rooks";
import { usePostAdImpressionMutation } from "@mwServices/Ads";

//Get static browser variables.
const browserPrefixes = ['moz', 'ms', 'o', 'webkit'];

const getHiddenPropertyName = (prefix) => (prefix ? prefix + 'Hidden' : 'hidden');
const getVisibilityEvent = (prefix) => (prefix ? prefix : '') + 'visibilitychange';

const getBrowserPrefix = () => {
    for (var i = 0; i < browserPrefixes.length; i++) {
        if (getHiddenPropertyName(browserPrefixes[i]) in document) {
            // return vendor prefix
            return browserPrefixes[i];
        }
    }
    return '';
}

const browserPrefix = getBrowserPrefix();
const hiddenPropertyName = getHiddenPropertyName(browserPrefix);
const visibilityEventName = getVisibilityEvent(browserPrefix);
const getIsDocumentHidden = () => !document[hiddenPropertyName];

interface ImpressionTimerProps {
    children: React.ReactNode;
    adId: number;
    sticky?: boolean;
};

const ImpressionTimer = ({ children, adId, sticky = false }: ImpressionTimerProps) => {
    const nodeRef = useRef<any>();
    const [postAdImpression, postAdImpressionResponse] = usePostAdImpressionMutation();

    const [firstImpression, setFirstImpression] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const [activeMS, setActiveMS] = useState(0);
    const [activeSeconds, setActiveSeconds] = useState(0);

    const fireTrackingEvent = (impressions) => {
        postAdImpression([adId, impressions]);
    };

    //Page Visibility;
    const [isPageVisible, setIsPageVisible] = useState(getIsDocumentHidden());
    const onPageVisibilityChange = () => {
        const checkPageVisible = getIsDocumentHidden();
        setIsPageVisible(checkPageVisible);
    }

    useLayoutEffect(() => {
        document.addEventListener(visibilityEventName, onPageVisibilityChange, false);
        document.addEventListener('focus', onPageVisibilityChange, false);
        document.addEventListener('blur', onPageVisibilityChange, false);
        window.addEventListener('focus', onPageVisibilityChange, false);
        window.addEventListener('blur', onPageVisibilityChange, false);

        return () => {
            document.removeEventListener(visibilityEventName, onPageVisibilityChange)
            // extra event listeners for better behaviour
            document.removeEventListener('focus', onPageVisibilityChange);
            document.removeEventListener('blur', onPageVisibilityChange);
            window.removeEventListener('focus', onPageVisibilityChange);
            window.removeEventListener('blur', onPageVisibilityChange);
        }
    }, []);

    //Element visibility.
    const isElementVisible = useIsVisible(nodeRef);

    useEffect(() => {
        const checkActive = isPageVisible && isElementVisible;
        if (checkActive !== isActive) setIsActive(checkActive)
    }, [isPageVisible, isElementVisible]);

    //Impressions logic.
    const intervalMS = 100;
    useIntervalWhen(() => {
        if (isActive) {
            const newTimeMS = activeMS + intervalMS;
            const newTimeS = Math.floor(newTimeMS / 1000);
            setActiveMS(newTimeMS);
            if (newTimeS !== activeSeconds) setActiveSeconds(newTimeS);

            //Every 15s fire impression.
            const modulo = ((newTimeMS / 15000) % 15) % 1;
            if(modulo === 0){
                fireTrackingEvent(1);
            }
        }
    }, intervalMS, isActive);

    // Reset if first impression inturrupted.
    useEffect(() => {
        if(!isActive && activeMS > 0 && activeSeconds < 15 && !firstImpression){
            setFirstImpression(true);
            fireTrackingEvent(1);
            setActiveMS(0);
            setActiveSeconds(0);
        }
    }, [isActive]);

    //reset when ad id changes.
    useEffect(() => {
        setIsActive(false);
        setActiveMS(0);
        setActiveSeconds(0);
    }, [adId]);

    return (<div ref={nodeRef} className={'impression-timer' + (sticky ? ' sticky' : '')}>{children}</div>);
};

export default ImpressionTimer;